import lscache from 'lscache';
import { ExperienceSource } from 'model/IExperience';
import { RecentlyViewedExperiencesCache } from 'services/RecentlyViewedExperiencesCache';
import Logger from 'utils/Logger';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { constructRecentDestinationSearchUrl } from '~/utils/recentSearches';

const RECENT_SEARCHES_KEY = 'recent_searches';
const MAX_RECENT_EXPERIENCES = 3;
const MAX_RECENT_DESTINATIONS = 3;

export type SearchType = 'experiences' | 'locals';

export type RecentlySearchedItem = {
  type: string;
  searchType: SearchType;
  title: string;
  subTitle: string;
  url: string;
  isNew?: boolean;
  addedAt?: number;
};

export type RecentDestinationSearch = {
  country: string;
  city?: string;
  searchType: SearchType;
  adults?: number;
  children?: number;
  dateFrom?: string;
  dateTo?: string;
  familyFriendly?: boolean;
  addedAt?: number;
};

export function getDestinationKey(
  destination: RecentDestinationSearch,
): string {
  return `${destination.country}|${destination.city ?? ''}|${destination.searchType}`;
}

export type RecentExperience = ExperienceSource & {
  addedAt: number;
};

function mapExperiencesToSearchItems(
  experiences: RecentExperience[],
): RecentlySearchedItem[] {
  return experiences
    .filter((item) => item.id)
    .map((item) => {
      const subCategory =
        item.subCategory || item.attributes?.subCategory || '';
      const area = item.area || item.attributes?.area || '';
      const subTitle = [subCategory, area].filter(Boolean).join(', ');

      return {
        type: 'tour',
        searchType: 'experiences' as const,
        title: item.title,
        subTitle,
        url: `/experience/${item.pretty_url}/`,
        addedAt: item.addedAt,
      };
    });
}

function mapDestinationsToSearchItems(
  destinations: RecentDestinationSearch[],
): RecentlySearchedItem[] {
  return destinations.map((destination) => {
    const url = constructRecentDestinationSearchUrl(destination);
    return {
      type: 'destination',
      searchType: destination.searchType ?? 'experiences',
      title: destination.city || destination.country,
      subTitle: destination.city ? destination.country : 'country',
      url,
      addedAt: destination.addedAt,
    };
  });
}

function orderRecentSearchItems(
  experiences: RecentExperience[],
  destinations: RecentDestinationSearch[],
): RecentlySearchedItem[] {
  const tours = mapExperiencesToSearchItems(experiences);
  const destinationItems = mapDestinationsToSearchItems(destinations);
  return [...tours, ...destinationItems].sort(
    (a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0),
  );
}

type RecentSearchesState = {
  recentlyViewedExperiences: RecentExperience[];
  recentDestinationSearches: RecentDestinationSearch[];
  recentSearchItems: RecentlySearchedItem[];
  init: () => void;
  addRecentlyViewedExperience: (experience: ExperienceSource) => void;
  addRecentDestinationSearch: (destination: RecentDestinationSearch) => void;
};

export const useRecentSearchesStore = create<RecentSearchesState>()(
  subscribeWithSelector((set, get) => ({
    recentlyViewedExperiences: [],
    recentDestinationSearches: [],
    recentSearchItems: [],

    init: () => {
      const rawExperiences =
        lscache.get(RecentlyViewedExperiencesCache.LS_KEY) ?? [];
      const rawDestinations = lscache.get(RECENT_SEARCHES_KEY) ?? [];

      const seenExperienceIds = new Set<string>();
      const experiences: RecentExperience[] = rawExperiences
        .map(
          (item: ExperienceSource & { addedAt?: number }, index: number) => ({
            ...item,
            addedAt: item.addedAt ?? Date.now() - index,
          }),
        )
        .filter((item: RecentExperience) => {
          if (!item.id || seenExperienceIds.has(item.id)) return false;
          seenExperienceIds.add(item.id);
          return true;
        });

      const seenDestinationKeys = new Set<string>();
      const destinations: RecentDestinationSearch[] = rawDestinations
        .map((item: RecentDestinationSearch, index: number) => ({
          ...item,
          addedAt: item.addedAt ?? Date.now() - index,
        }))
        .filter((item: RecentDestinationSearch) => {
          const key = getDestinationKey(item);
          if (seenDestinationKeys.has(key)) return false;
          seenDestinationKeys.add(key);
          return true;
        });

      lscache.set(RecentlyViewedExperiencesCache.LS_KEY, experiences);
      lscache.set(RECENT_SEARCHES_KEY, destinations);

      set({
        recentlyViewedExperiences: experiences,
        recentDestinationSearches: destinations,
        recentSearchItems: orderRecentSearchItems(experiences, destinations),
      });
    },

    addRecentlyViewedExperience: (experience) => {
      if (!experience) {
        Logger.error(
          ' Tried to add invalid experience to recently viewed',
          JSON.stringify(experience),
        );
        return;
      }
      const current = get().recentlyViewedExperiences;
      const updated = [
        { ...experience, addedAt: Date.now() },
        ...current.filter((item) => item.id !== experience.id),
      ].slice(0, MAX_RECENT_EXPERIENCES);

      lscache.set(RecentlyViewedExperiencesCache.LS_KEY, updated);
      const destinations = get().recentDestinationSearches;
      set({
        recentlyViewedExperiences: updated,
        recentSearchItems: orderRecentSearchItems(updated, destinations),
      });
    },

    addRecentDestinationSearch: (destination) => {
      if (!destination.country) return;
      const current = get().recentDestinationSearches;
      const destinationKey = getDestinationKey(destination);
      const updated = [
        { ...destination, addedAt: Date.now() },
        ...current.filter((d) => getDestinationKey(d) !== destinationKey),
      ].slice(0, MAX_RECENT_DESTINATIONS);

      lscache.set(RECENT_SEARCHES_KEY, updated);
      const experiences = get().recentlyViewedExperiences;
      set({
        recentDestinationSearches: updated,
        recentSearchItems: orderRecentSearchItems(experiences, updated),
      });
    },
  })),
);
